import { useSession } from "next-auth/react";
import { useMemo } from "react";
import { useUserPermissions as useUserPermissionsQuery, useCanAccessCompany } from "@/hooks/api/use-permissions";
import { useCurrentCompany } from "@/hooks/api/use-companies";

// Recursos básicos que todos os usuários autenticados de empresas parceiras podem acessar
const BASIC_PARTNER_RESOURCES = ["simulacao", "propostas", "dashboard", "clientes", "funil", "configuracoes"];

// Recursos que usuários com perfil "master" ou superior podem acessar
const MASTER_ROLE_RESOURCES = ["acessos", "configuracoes"];

// Recursos bloqueados para digitador (só pode ver simulacao e propostas)
const DIGITADOR_BLOCKED_RESOURCES = ["acessos", "funil", "clientes", "configuracoes"];

/**
 * Hook para verificar se o usuário tem uma permissão específica
 * OTIMIZADO: Verifica permissões localmente baseado em roles, sem chamar backend
 */
export function useHasPermission(resource: string, action: string, companyId?: string) {
  const { data: session } = useSession();
  const { data: company } = useCurrentCompany();

  return useMemo(() => {
    if (!session) return false;

    const userRoles = session.user?.roles ?? [];
    const isSuperAdmin = userRoles.includes("super_admin");
    const isAdminInterno = userRoles.includes("admin_interno");
    const isMasterRole = userRoles.includes("master");
    const isDigitador = userRoles.includes("digitador");
    const isMasterCompany = company?.isMaster === true;

    // Super Admin tem todas as permissões
    if (isSuperAdmin) return true;

    // Admin Interno da empresa master (Topamais) tem todas as permissões
    if (isAdminInterno && isMasterCompany) return true;

    // Digitador é bloqueado de recursos específicos (mesmo que tenha outras roles)
    // Só bloqueia se for APENAS digitador (sem master, admin_interno ou super_admin)
    if (isDigitador && !isMasterRole && !isAdminInterno && !isSuperAdmin) {
      if (DIGITADOR_BLOCKED_RESOURCES.includes(resource)) {
        return false;
      }
    }

    // Para recursos básicos (dashboard, simulação, propostas, clientes, funil, configuracoes)
    // permitir acesso a qualquer usuário autenticado
    if (BASIC_PARTNER_RESOURCES.includes(resource) && action === "read") {
      return true;
    }

    // Usuários com perfil "master" de qualquer empresa podem acessar recursos específicos (ex: acessos)
    if (isMasterRole && MASTER_ROLE_RESOURCES.includes(resource)) {
      return true;
    }

    // Usuários com perfil "master" podem criar/editar/deletar em recursos básicos
    if (isMasterRole && BASIC_PARTNER_RESOURCES.includes(resource)) {
      return true;
    }

    // Por padrão, permitir acesso de leitura aos recursos básicos
    if (BASIC_PARTNER_RESOURCES.includes(resource) && action === "read") {
      return true;
    }

    // Demais casos: negar acesso (pode ser expandido no futuro com RBAC completo)
    return false;
  }, [session, company, resource, action]);
}

/**
 * Hook para verificar se o usuário tem acesso a um recurso
 */
export function useHasResourceAccess(resource: string, companyId?: string) {
  return useHasPermission(resource, "read", companyId);
}

/**
 * Hook para obter todas as permissões do usuário
 */
export function useUserPermissionsList(companyId?: string) {
  const { data: session } = useSession();
  const { data: permissions } = useUserPermissionsQuery(companyId);

  return useMemo(() => {
    if (!session) return [];
    const userRoles = session.user?.roles ?? [];
    const isSuperAdmin = userRoles.includes("super_admin");
    
    // Super Admin tem todas as permissões (será carregado do backend)
    if (isSuperAdmin) {
      return permissions ?? [];
    }
    
    // Admin interno agora segue as regras normais de RBAC
    return permissions ?? [];
  }, [session, permissions]);
}

/**
 * Hook para verificar se o usuário pode acessar uma empresa
 */
export function useCanAccessCompanyHook(targetCompanyId: string) {
  const { data: session } = useSession();
  const { data: canAccess } = useCanAccessCompany(
    targetCompanyId,
    !!session && !!targetCompanyId
  );

  return useMemo(() => {
    if (!session) return false;
    const userRoles = session.user?.roles ?? [];
    const isSuperAdmin = userRoles.includes("super_admin");
    
    // Super Admin tem acesso a todas empresas
    if (isSuperAdmin) return true;
    
    // Admin interno agora segue as regras normais de acesso a empresas
    const currentCompanyId = session.user?.currentCompanyId;
    if (!currentCompanyId) return false;
    
    // Verificar se empresa atual é master (será verificado no backend)
    return canAccess ?? false;
  }, [session, canAccess]);
}

/**
 * Hook para verificar se a empresa atual é Topamais (master)
 */
export function useIsMasterCompany() {
  const { data: session } = useSession();
  const { data: company } = useCurrentCompany();

  return useMemo(() => {
    if (!session || !company) return false;
    return company.isMaster ?? false;
  }, [session, company]);
}

